import React, { useEffect, useState } from 'react'
import ToastMessage from '../../../../../common/ToastMessage';
import CustomDataGrid from '../../../../../common/CustomDataGrid';
import usePR from '../../../../../store/ReactQueries/procurements/purchaseRequests/usePR';
import useDeletePR from '../../../../../store/ReactQueries/procurements/purchaseRequests/useDeletePR';
import { GridColDef } from '@mui/x-data-grid';
import { PRStatus } from '../../../../../enums/PRStatus';
import { PAGESIZE } from '../../../../../utils/constants';
import { FieldErrors } from '../../../../../domain/helpers/Errors';
import { useLanguageStore } from '../../../../../store/useLanguageStore';
import { ReactComponent as PDFLogo } from '../../../../../images/pdf-icon.svg';
import { PRInterface } from '../../../../../models/ProcurementModule/PurchaseRequest';

export const PR = () => {
    const { language } = useLanguageStore();

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(PAGESIZE);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [form, setForm] = useState<PRInterface>({
        id: '',
        description: '',
        quantity: 1,
        reason: '',
        status: PRStatus.Pending,
        purchaseOrderTypeId: '',
        isUrgent: false,
    });
    const [modal, setModal] = useState<{ add: boolean, edit: boolean, view: boolean }>({
        add: false,
        edit: false,
        view: false
    });
    let { data: purchaseRequests, error: purchaseRequestsError, isLoading } = usePR(page, pageSize);
    const deletePR = useDeletePR(page, pageSize);
    const handleDownloadPDF = async (id: string, documentUrl: string) => {
        if (id && documentUrl) {
            try {
                const link = document.createElement("a");
                link.href = documentUrl;
                link.download = "document.pdf";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

            } catch (error) {
                console.error("Error downloading PDF:", error);
            }
        }
    };

    useEffect(() => {
        if (purchaseRequestsError) ToastMessage.Failed(`Error: ${purchaseRequestsError.message}`);
    }, [purchaseRequestsError]);

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Id', type: "string", editable: false, width: 150 },
        { field: 'code', headerName: 'Code', type: "string", editable: false, width: 120 },
        { field: 'description', headerName: 'Description', type: "string", editable: false, width: 200 },
        { field: 'quantity', headerName: 'Quantity', type: "string", editable: false, width: 100 },
        { field: 'reason', headerName: 'Reason', type: "string", editable: false, width: 150 },
        { field: 'status', headerName: 'Status', type: "string", editable: false, width: 150 },
        {
            field: 'purchaseOrderType',
            headerName: 'Purchase order type',
            type: "string",
            editable: false,
            width: 130,
            renderCell: ({ row }) => {
                const purchaseOrderTypes = row && row.purchaseOrderType ? row.purchaseOrderType.title : '';
                return <div className="d-flex align-items-center justify-content-between" title={purchaseOrderTypes}>
                    <span className="text-truncate" style={{ maxWidth: 100 }}>{purchaseOrderTypes}</span>
                </div>
            }
        },
        { field: 'purchaseOrderTypeId', headerName: 'PurchaseOrderTypeId', type: "string", editable: false, width: 200 },
        { field: 'isUrgent', headerName: 'Urgent', type: "boolean", editable: false, width: 80 },
        {
            field: 'documentUrl',
            headerName: 'DocumentTemplate',
            type: "string",
            editable: false,
            width: 80,
            renderCell: ({ row }) => {
                return row.documentUrl
                    ? <div className="d-flex align-items-center justify-content-between">
                        <div
                            className='pdf-icon-download'
                            role='button'
                            title={'Download template'}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDownloadPDF(row.id, row.documentUrl)
                            }}
                        >
                            <PDFLogo width="30" height="30" />
                        </div>
                    </div>
                    : null
            }
        },
    ];

    const addItems = () => {
        setForm({ ...form, id: '', description: '', code: '' });
        setModal({ ...modal, add: true });
        setErrors({});
    }

    const handleError = (error: unknown) => {
        if (error instanceof Error) ToastMessage.Failed(error.message);
        else ToastMessage.Failed();
    };

    const handleDelete = async (id: string, confirmation: boolean = true) => {
        const proceed = confirmation
            ? await ToastMessage.Confirm(language['ToastDeleteItem'])
            : true;

        if (proceed) {
            deletePR.mutate(id, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }
    };

    const showColumnModal = ['description', 'code', 'date', 'reason'];
    const hideColumns = ['id', 'purchaseOrderTypeId'];

    return <div className='d-flex flex-row transition-wrapper'>
        <div className="content">
            <CustomDataGrid
                name={language['PurchaseRequests']}
                newAddComponent="PurchaseRequests"
                rows={purchaseRequests || []}
                form={form}
                page={page}
                modal={modal}
                errors={errors}
                columns={columns}
                loading={isLoading}
                pageSize={pageSize}
                hideColumns={hideColumns}
                showColumnModal={showColumnModal}
                setPage={setPage}
                setForm={setForm}
                setModal={setModal}
                addItems={addItems}
                setErrors={setErrors}
                setPageSize={setPageSize}
                handleDelete={handleDelete}
                handleSave={function (): Promise<void> {
                    throw new Error('Function not implemented.');
                }} />
        </div>
    </div>
}
export default PR;